import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { parseNaturalTaskText } from "../shared/utils/parseNaturalTask";
import { getDueDateForApi } from "../shared/utils/dueDateTime";
import { getMessageForFetchError } from "../shared/utils/apiError";
import { createTasks } from "../shared/api/tasks";

/**
 * Add Task (NLP) modal: parse natural language text into one or more tasks
 * (one task per non-empty line).
 *
 * Props:
 * - open: boolean
 * - accountId: string
 * - createdBy: string
 * - onClose: () => void
 * - labelsForNewItem?: string[] // labels assigned when creating from an active label filter
 * - onCreated?: (autoSchedule: boolean, createdCount: number) => void
 * - showAutoScheduleToggle?: boolean  // when true, shows per-task auto schedule toggle
 */
function SmartAddModal({ open, accountId, createdBy, onClose, onCreated, showAutoScheduleToggle = false, othersProjectId = "", labelsForNewItem }) {
  const [naturalText, setNaturalText] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [autoScheduleEnabled, setAutoScheduleEnabled] = useState(true);

  useEffect(() => {
    if (!open) {
      setNaturalText("");
      setError(null);
      setSubmitting(false);
      setAutoScheduleEnabled(true);
    }
  }, [open]);

  if (!open) return null;

  const lineCount = (naturalText || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean).length;

  const handleCreate = async () => {
    const lines = (naturalText || "")
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length === 0) {
      setError("Enter a task description.");
      return;
    }
    if (!accountId) {
      setError("Missing account information.");
      return;
    }
    setError(null);

    const payloads = [];

    for (let i = 0; i < lines.length; i++) {
      const parsed = parseNaturalTaskText(lines[i]);
      if (!parsed.name) {
        setError(`Could not extract a task name from line ${i + 1}.`);
        return;
      }

      const repeat_rule = parsed.repeat_rule || null;
      const due_date =
        getDueDateForApi(parsed.due_date, parsed.due_time || "") ||
        getDueDateForApi(dayjs().format("YYYY-MM-DD"), "");

      payloads.push({
        name: parsed.name,
        due_date,
        amount:
          parsed.amount != null && Number(parsed.amount) >= 0
            ? Number(parsed.amount)
            : 0,
        repeat_rule,
        ...(othersProjectId && String(othersProjectId).trim() !== ""
          ? { project_id: String(othersProjectId).trim() }
          : {}),
        ...(Array.isArray(labelsForNewItem) && labelsForNewItem.length > 0
          ? { labels: labelsForNewItem }
          : {}),
      });
    }

    setSubmitting(true);
    try {
      const { created } = await createTasks(accountId, payloads, createdBy);
      if (created > 0) {
        if (onCreated) onCreated(autoScheduleEnabled, created);
        if (onClose) onClose();
      } else {
        setError(lines.length === 1 ? "Task was not created." : "Tasks were not created.");
      }
    } catch (err) {
      setError(
        getMessageForFetchError(
          err,
          lines.length === 1 ? "Unable to create task." : "Unable to create tasks."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const createLabel =
    submitting
      ? "Creating…"
      : lineCount <= 1
        ? "Create task"
        : `Create ${lineCount} tasks`;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
        padding: "12px",
        boxSizing: "border-box",
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="smart-add-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) onClose();
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          maxWidth: "520px",
          width: "100%",
          maxHeight: "calc(100vh - 24px)",
          overflowY: "auto",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
          padding: "18px 20px 16px",
          boxSizing: "border-box",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "8px",
            flexWrap: "wrap",
            marginBottom: "10px",
          }}
        >
          <h3
            id="smart-add-modal-title"
            style={{
              margin: 0,
              fontSize: "1rem",
              fontWeight: 600,
              color: "#212529",
              flex: "1 1 auto",
              minWidth: 0,
            }}
          >
            Add Task (NLP)
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            {showAutoScheduleToggle && (
              <label
                title="When enabled, newly created tasks will be automatically scheduled into the earliest open slots."
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: submitting ? "not-allowed" : "pointer",
                  userSelect: "none",
                  margin: 0,
                  fontSize: 12,
                  color: "#57606a",
                }}
              >
                <input
                  type="checkbox"
                  checked={autoScheduleEnabled}
                  onChange={(e) => setAutoScheduleEnabled(e.target.checked)}
                  disabled={submitting}
                  style={{ margin: 0 }}
                  aria-label="Auto schedule newly created NLP tasks"
                />
                <span>Auto schedule</span>
              </label>
            )}
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "18px",
                  lineHeight: 1,
                  padding: "2px 4px",
                  color: "#6c757d",
                }}
              >
                ×
              </button>
            )}
          </div>
        </div>

        <p
          style={{
            margin: "0 0 8px 0",
            fontSize: "13px",
            color: "#6c757d",
            overflowWrap: "anywhere",
          }}
        >
          Describe tasks in plain English, one per line. We extract name, date,
          time, amount, and repeat.
        </p>

        {error && (
          <div
            style={{
              fontSize: "13px",
              color: "#b02a37",
              marginBottom: "8px",
              padding: "6px 8px",
              background: "#f8d7da",
              border: "1px solid #f5c2c7",
              borderRadius: "4px",
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <textarea
            value={naturalText}
            onChange={(e) => setNaturalText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleCreate();
              }
            }}
            rows={5}
            placeholder="e.g. Doctor appointment Thursday at 4pm, $100, monthly"
            aria-label="Task descriptions, one per line"
            style={{
              width: "100%",
              minWidth: 0,
              minHeight: "96px",
              padding: "6px 8px",
              fontSize: "13px",
              lineHeight: 1.4,
              borderRadius: "4px",
              border: "1px solid #ced4da",
              boxSizing: "border-box",
              resize: "vertical",
              fontFamily: "inherit",
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            marginTop: "14px",
            flexWrap: "wrap",
          }}
        >
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                padding: "6px 12px",
                fontSize: "12px",
                borderRadius: "4px",
                border: "1px solid #dee2e6",
                background: "#fff",
                color: "#495057",
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleCreate}
            disabled={lineCount === 0 || submitting}
            style={{
              padding: "6px 14px",
              fontSize: "12px",
              borderRadius: "4px",
              border: "none",
              background: "#0d6efd",
              color: "#fff",
              cursor: submitting ? "not-allowed" : "pointer",
              fontWeight: 500,
            }}
          >
            {createLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SmartAddModal;
